/**
 * Table canon (KB mira-safe/риски-ui-таблица-и-мёртвые):
 * single-risk → one row/controller; multi-risk → one row/hit + ▶ for #1–#4;
 * collapsed metrics follow filter/summaryRiskNumber; expanded root = path only.
 *
 * ℹ MUST open RiskCaseDetailModal → RiskCaseCard (same layout as cards view).
 * Card layout contract: `./riskCardLayoutCanon.js` — do not invent a table-only UI.
 */
import { Fragment, useMemo, useRef, useState } from 'react';
import CommentsNotesButton from './CommentsNotesButton';
import DeviceHistoryLinkButton from './DeviceHistoryLinkButton';
import { resolveCommentDeviceId } from './DeviceCommentsPanel';
import {
  buildCaseHeaderRowCells,
  buildCasePath,
  buildCaseRowCells,
  buildHitRowCells,
  buildHitRowLabel,
  pickSummaryHit,
} from './riskCaseRowData';
import {
  buildCaseExpandSlots,
  buildEmptyHitRowCells,
  buildEmptyHitRowLabel,
  buildEmptyTabKey,
  buildHitTabKey,
} from './riskCaseTabSlots';

/** Window rows when page is large; disabled while any row is expanded/comments open. */
const VIRTUALIZE_FROM = 40;
const ESTIMATED_ROW_PX = 44;
const TABLE_VIEWPORT_PX = 640;

const COLUMNS = [
  { key: 'path', label: 'Путь' },
  { key: 'serial', label: 'Серийный номер' },
  { key: 'connection', label: 'Связь' },
  { key: 'firmware', label: 'Прош.' },
  { key: 'schedule', label: 'График' },
  { key: 'integrations', label: 'Интегр.' },
  { key: 'riskLevel', label: 'Риск' },
  { key: 'score', label: 'Score' },
  { key: 'overdue', label: 'Проср./сигнал' },
  { key: 'missedTx', label: 'Проп. перед.', title: 'Пропущено передач' },
  { key: 'info', label: 'ℹ' },
];

const TABLE_COL_SPAN = 1 + COLUMNS.length;

/** Muted transparent row tint matching the risk score scale. */
const RISK_ROW_TINT = {
  ok: 'bg-emerald-400/[0.08] hover:bg-emerald-400/[0.12]',
  low: 'bg-sky-400/[0.08] hover:bg-sky-400/[0.12]',
  medium: 'bg-amber-400/[0.10] hover:bg-amber-400/[0.14]',
  high: 'bg-orange-400/[0.12] hover:bg-orange-400/[0.16]',
  very_high: 'bg-red-400/[0.12] hover:bg-red-400/[0.16]',
};

function riskRowClass(riskLevel) {
  return RISK_ROW_TINT[riskLevel] || 'hover:bg-slate-50 dark:hover:bg-slate-900/40';
}

const stickyHead =
  'sticky left-0 z-20 bg-slate-50 dark:bg-slate-900/95';
const stickyCell =
  'sticky left-0 z-10 bg-white dark:bg-slate-950/95';

function TableCells({ cells, pathTitle, pathClassName = '', stickyPath = false }) {
  return (
    <>
      <td
        className={`min-w-[12rem] max-w-[20rem] px-3 py-2 sm:min-w-[16rem] sm:max-w-[24rem] ${
          stickyPath ? stickyCell : ''
        } ${pathClassName}`}
        title={pathTitle || cells.path}
      >
        <span className="block truncate">{pathTitle ?? cells.path}</span>
      </td>
      <td className="whitespace-nowrap px-3 py-2 font-mono text-[11px]">{cells.serial}</td>
      <td className="whitespace-nowrap px-3 py-2">{cells.connection}</td>
      <td className="whitespace-nowrap px-3 py-2">{cells.firmware}</td>
      <td className="whitespace-nowrap px-3 py-2" title={cells.scheduleTitle || undefined}>
        {cells.schedule}
      </td>
      <td className="max-w-[10rem] truncate px-3 py-2" title={cells.integrations}>
        {cells.integrations}
        {cells.hasComment ? (
          <span className="ml-1 rounded bg-sky-400/15 px-1 py-0.5 text-[10px] text-sky-700 dark:text-sky-300">
            комм.
          </span>
        ) : null}
      </td>
      <td className="whitespace-nowrap px-3 py-2">{cells.riskLevel}</td>
      <td className="whitespace-nowrap px-3 py-2">{cells.score}</td>
      <td className="whitespace-nowrap px-3 py-2">{cells.overdue}</td>
      <td
        className="whitespace-nowrap px-3 py-2 tabular-nums"
        title={cells.missedTxTitle || 'Пропущено передач'}
      >
        {cells.missedTx}
      </td>
    </>
  );
}

export default function RiskCasesTable({
  cases,
  organizationLabel,
  /** Risk ids from dashboard filter; collapsed root shows this risk (max among them). */
  summaryRiskNumbers = null,
  expandedKeys,
  onToggleExpanded,
  onOpenDetails,
  openCommentsKey = null,
  onToggleComments,
  onOpenDeviceHistory = null,
}) {
  const expandedSet = useMemo(() => new Set(expandedKeys || []), [expandedKeys]);
  const scrollerRef = useRef(null);
  const [scrollTop, setScrollTop] = useState(0);
  const hasOpenRow = expandedSet.size > 0 || openCommentsKey != null;
  const useWindow =
    !hasOpenRow && Array.isArray(cases) && cases.length >= VIRTUALIZE_FROM;

  const visibleCases = useMemo(() => {
    if (!useWindow) {
      return cases;
    }
    const overscan = 8;
    const first = Math.max(0, Math.floor(scrollTop / ESTIMATED_ROW_PX) - overscan);
    const count = Math.ceil(TABLE_VIEWPORT_PX / ESTIMATED_ROW_PX) + overscan * 2;
    return cases.slice(first, first + count).map((riskCase, offset) => ({
      riskCase,
      padTop: offset === 0 ? first * ESTIMATED_ROW_PX : 0,
      padBottom:
        offset === Math.min(count, cases.length - first) - 1
          ? Math.max(0, (cases.length - (first + count)) * ESTIMATED_ROW_PX)
          : 0,
    }));
  }, [cases, scrollTop, useWindow]);

  return (
    <div
      ref={scrollerRef}
      className="overflow-auto rounded-2xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-950/60"
      style={useWindow ? { maxHeight: TABLE_VIEWPORT_PX } : undefined}
      onScroll={useWindow ? (event) => setScrollTop(event.currentTarget.scrollTop) : undefined}
    >
      <table className="min-w-full border-collapse text-left text-xs text-slate-800 dark:text-slate-200">
        <thead className="sticky top-0 z-30 border-b border-slate-200 bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500 dark:border-slate-800 dark:bg-slate-900/95 dark:text-slate-400">
          <tr>
            <th className={`w-10 px-2 py-2 ${stickyHead}`} aria-label="Раскрыть" />
            {COLUMNS.map((column) => (
              <th
                key={column.key}
                className={`px-3 py-2 font-medium ${column.key === 'path' ? stickyHead : ''}`}
                style={column.key === 'path' ? { left: '2.5rem' } : undefined}
                title={column.title || undefined}
              >
                {column.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {(useWindow ? visibleCases : cases.map((riskCase) => ({ riskCase, padTop: 0, padBottom: 0 }))).map(
            ({ riskCase, padTop, padBottom }) => {
            const rowSummaryRisks =
              riskCase.summaryRiskNumber != null
                ? [riskCase.summaryRiskNumber]
                : summaryRiskNumbers;
            const summaryHit = pickSummaryHit(riskCase.hits, rowSummaryRisks);
            const rootCells = buildCaseRowCells(riskCase, rowSummaryRisks);
            const fullPath = buildCasePath(riskCase.header, organizationLabel);
            const expandSlots = buildCaseExpandSlots(riskCase.hits, summaryHit);
            const canExpand = expandSlots.length > 0;
            const isExpanded = expandedSet.has(riskCase.key);
            const commentsOpen = openCommentsKey != null && openCommentsKey === riskCase.key;
            const displayRootCells = isExpanded
              ? buildCaseHeaderRowCells(fullPath)
              : { ...rootCells, path: fullPath };

            return (
              <Fragment key={riskCase.key}>
                {padTop > 0 ? (
                  <tr aria-hidden="true">
                    <td colSpan={TABLE_COL_SPAN} style={{ height: padTop, padding: 0, border: 0 }} />
                  </tr>
                ) : null}
                <tr
                  className={`border-b border-slate-100 dark:border-slate-900/80 ${
                    isExpanded
                      ? 'bg-slate-50/80 hover:bg-slate-50 dark:bg-slate-900/50 dark:hover:bg-slate-900/60'
                      : riskRowClass(summaryHit?.rule_result?.risk_level)
                  }`}
                >
                  <td className={`px-2 py-2 align-top ${stickyCell}`}>
                    {canExpand ? (
                      <button
                        type="button"
                        aria-expanded={isExpanded}
                        aria-label={isExpanded ? 'Свернуть риски' : 'Показать все риски'}
                        onClick={() => onToggleExpanded(riskCase.key)}
                        className="inline-flex min-h-10 min-w-10 items-center justify-center rounded text-slate-500 hover:text-slate-800 dark:hover:text-slate-200 sm:min-h-0 sm:min-w-0 sm:px-1"
                      >
                        {isExpanded ? '▼' : '▶'}
                      </button>
                    ) : null}
                  </td>
                  <TableCells
                    cells={displayRootCells}
                    pathTitle={fullPath}
                    stickyPath
                    pathClassName={`!left-10 ${isExpanded ? 'font-medium text-slate-800 dark:text-slate-100' : ''}`}
                  />
                  <td className="px-3 py-2 align-top">
                    <div className="flex items-start gap-1">
                      <button
                        type="button"
                        aria-label="Подробности кейса"
                        onClick={() => onOpenDetails(riskCase)}
                        className="inline-flex h-6 w-6 min-h-10 min-w-10 items-center justify-center rounded-full border border-slate-300 text-[11px] text-slate-600 hover:border-sky-400 hover:text-sky-700 dark:border-slate-700 dark:text-slate-300 dark:hover:text-sky-200 sm:min-h-0 sm:min-w-0"
                      >
                        ℹ
                      </button>
                      {onToggleComments ? (
                        <CommentsNotesButton open={commentsOpen} onClick={() => onToggleComments(riskCase)} />
                      ) : null}
                      <DeviceHistoryLinkButton
                        deviceId={resolveCommentDeviceId(riskCase)}
                        serialNumber={
                          riskCase.header?.controller_serial || rootCells.serial || undefined
                        }
                        onOpen={onOpenDeviceHistory}
                      />
                    </div>
                  </td>
                </tr>

                {isExpanded
                  ? expandSlots.map((slot) => {
                      if (slot.empty) {
                        const emptyCells = {
                          ...buildEmptyHitRowCells(slot.riskNumber),
                          path: buildEmptyHitRowLabel(slot.riskNumber),
                        };
                        return (
                          <tr
                            key={`${riskCase.key}-${buildEmptyTabKey(slot.riskNumber)}`}
                            className="border-b border-slate-100 opacity-70 dark:border-slate-900/80"
                          >
                            <td className={stickyCell} />
                            <TableCells
                              cells={emptyCells}
                              pathTitle={emptyCells.path}
                              stickyPath
                              pathClassName="!left-10 pl-4 text-slate-400 dark:text-slate-500"
                            />
                            <td />
                          </tr>
                        );
                      }

                      const hit = slot.hit;
                      const hitLabel = buildHitRowLabel(hit);
                      const hitCells = { ...buildHitRowCells(hit), path: hitLabel };
                      return (
                        <tr
                          key={`${riskCase.key}-${buildHitTabKey(hit)}`}
                          className={`border-b border-slate-100 dark:border-slate-900/80 ${riskRowClass(
                            hit.rule_result?.risk_level,
                          )}`}
                        >
                          <td className={stickyCell} />
                          <TableCells
                            cells={hitCells}
                            pathTitle={hitLabel}
                            stickyPath
                            pathClassName="!left-10 pl-4 text-slate-600 dark:text-slate-300"
                          />
                          <td className="px-3 py-2 align-top">
                            <button
                              type="button"
                              aria-label={`Подробности риска #${hit.risk_number}`}
                              onClick={() =>
                                onOpenDetails({
                                  ...riskCase,
                                  summaryRiskNumber: Number(hit.risk_number),
                                })
                              }
                              className="inline-flex h-6 w-6 min-h-10 min-w-10 items-center justify-center rounded-full border border-slate-300 text-[11px] text-slate-600 hover:border-sky-400 hover:text-sky-700 dark:border-slate-700 dark:text-slate-300 dark:hover:text-sky-200 sm:min-h-0 sm:min-w-0"
                            >
                              ℹ
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  : null}

                {padBottom > 0 ? (
                  <tr aria-hidden="true">
                    <td colSpan={TABLE_COL_SPAN} style={{ height: padBottom, padding: 0, border: 0 }} />
                  </tr>
                ) : null}
              </Fragment>
            );
          },
          )}
        </tbody>
      </table>
    </div>
  );
}
